import {useState} from "react";
import moment from "moment";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Select, TextField, FormControl, InputLabel, MenuItem,
  Grid, Chip, CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  Box,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import {asyncRequest} from "src/services/network";
import {alert} from "src/services/alert";
import UserInfo from "src/components/common/UserInfo";
import UserInfoDrawer from "src/components/common/UserInfoDrawer";

const TABLE_HEAD = [
  {id: "uid", label: "用户", alignRight: false},
  {id: "isValid", label: "VIP状态", alignRight: false},
  {id: "beginTime", label: "生效时间", alignRight: false},
  {id: "endTime", label: "VIP到期时间", alignRight: false},
  {id: "robotExpireTime", label: "机器人到期时间", alignRight: false},
  {id: "joinType", label: "加入方式", alignRight: false},
  {id: "remark", label: "备注", alignRight: false},
  {id: "records", label: "拨付记录", alignRight: false},
];

const STATUS_OPTIONS = [
  {value: "", label: "全部"},
  {value: "valid", label: "生效中"},
  {value: "expired", label: "已过期"},
];

const JOIN_TYPE_OPTIONS = [
  {value: "", label: "全部"},
  {value: "ADMIN", label: "后台拨付"},
  {value: "PAYMENT", label: "充值购买"},
  {value: "EXCHANGE", label: "兑换"},
];

const JOIN_TYPE_MAP = {ADMIN: "后台拨付", PAYMENT: "充值购买", EXCHANGE: "兑换"};
const ACTION_MAP = {GRANT: "开通", EXTEND: "延期", REVOKE: "撤销"};
const RECORD_JOIN_TYPE_MAP = {ADMIN: "后台拨付", PAYMENT: "充值购买"};

const RECORD_TABLE_HEAD = [
  {id: "action", label: "操作", alignRight: false},
  {id: "joinType", label: "方式", alignRight: false},
  {id: "days", label: "天数", alignRight: false},
  {id: "prevEndTime", label: "操作前到期", alignRight: false},
  {id: "newEndTime", label: "操作后到期", alignRight: false},
  {id: "adminName", label: "操作人", alignRight: false},
  {id: "remark", label: "备注", alignRight: false},
  {id: "createdAt", label: "操作时间", alignRight: false},
];

function formatTimestamp(ts) {
  if (!ts) return "-";
  // 秒级时间戳
  if (typeof ts === 'number' && ts < 1e12) return moment.unix(ts).format("YYYY-MM-DD HH:mm:ss");
  return moment(ts).format("YYYY-MM-DD HH:mm:ss");
}

function RobotExpireCell({robotExpireTime, robotStatus, vipEndTime}) {
  const robotValid = robotStatus === 1 && robotExpireTime && (robotExpireTime * 1000) > Date.now();
  const vipValid = vipEndTime && new Date(vipEndTime) > new Date();

  // 颜色比对逻辑
  let color = "default"; // 灰色=均未开通
  if (vipValid && robotValid) {
    // 比较日期是否一致
    const vipDate = moment(vipEndTime).format("YYYY-MM-DD");
    const robotDate = moment.unix(robotExpireTime).format("YYYY-MM-DD");
    color = vipDate === robotDate ? "success" : "error"; // 绿=一致 红=不一致
  } else if (vipValid || robotValid) {
    color = "error"; // 一端有一端无=红
  }

  return (
    <Typography variant="body2" color={color === "success" ? "success.main" : color === "error" ? "error.main" : "text.secondary"}>
      {robotExpireTime ? formatTimestamp(robotExpireTime) : "未开通"}
    </Typography>
  );
}

export default function JytVipList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [filter, setFilter] = useState({
    page: 0, size: 20, uid: "", status: "", joinType: "",
  });

  // VIP记录弹框
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordUid, setRecordUid] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);

  // 用户详情弹框
  const [targetUser, setTargetUser] = useState(null);

  async function getItems(filterOverride) {
    setItems(null);
    const queryFilter = filterOverride || filter;
    const {data, count} = await asyncRequest({
      path: "/jingxi/jyt-vip/list",
      data: queryFilter,
    });
    setItems(data || []);
    setTotal(count);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = {...filter, page: 0, size: newSize};
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = {...filter, page: newPage};
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({page: 0, size: 20, uid: "", status: "", joinType: ""});
  }

  async function openRecords(uid) {
    setRecordUid(uid);
    setRecordOpen(true);
    setRecordLoading(true);
    const {data} = await asyncRequest({
      path: "/jingxi/jyt-vip/records",
      data: {uid},
    });
    setRecords(data || []);
    setRecordLoading(false);
  }

  async function handleExport() {
    const {msg} = await asyncRequest({
      path: "/jingxi/jyt-vip/list/export",
      data: filter,
    });
    alert(msg, "success");
  }

  const isNotFound = items && items.length === 0;

  return (
    <Page title="金银通VIP列表">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            金银通VIP列表
          </Typography>
        </Stack>

        <Card>
          <ListToolbar showSearch={false} onReset={handleReset} onRefresh={() => getItems()}>
            <Grid container rowGap={2} alignItems="center">
              <Grid item sm={6} md={3}>
                <TextField
                  size="small" style={{width: 200}} label="鲸世优选UID"
                  placeholder="输入UID" autoComplete="off"
                  value={filter.uid} onChange={(e) => setFilter({...filter, uid: e.target.value})}
                />
              </Grid>
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>VIP状态</InputLabel>
                  <Select label="VIP状态" value={filter.status}
                    onChange={(e) => setFilter({...filter, status: e.target.value})} style={{width: 200}}>
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <FormControl size="small">
                  <InputLabel>加入方式</InputLabel>
                  <Select label="加入方式" value={filter.joinType}
                    onChange={(e) => setFilter({...filter, joinType: e.target.value})} style={{width: 200}}>
                    {JOIN_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6} md={3}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={() => getItems()}>查询</Button>
                  <Button variant="outlined" size="small" onClick={handleExport} disabled={!hasQueried}>导出</Button>
                </Stack>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer sx={{minWidth: 1000}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                {!hasQueried ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="text.secondary">请设置查询条件后点击查询</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                          <CircularProgress/>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => (
                      <TableRow hover key={row.uid} tabIndex={-1}>
                        <TableCell align="left">
                          <UserInfo uid={row.uid} nickname={row.nickname} onClick={() => setTargetUser({uid: row.uid})}/>
                        </TableCell>
                        <TableCell align="left">
                          <Chip size="small" label={row.isValid ? "生效中" : "已过期"} color={row.isValid ? "success" : "default"}/>
                        </TableCell>
                        <TableCell align="left">{formatTimestamp(row.beginTime)}</TableCell>
                        <TableCell align="left">{formatTimestamp(row.endTime)}</TableCell>
                        <TableCell align="left">
                          <RobotExpireCell
                            robotExpireTime={row.robotExpireTime}
                            robotStatus={row.robotStatus}
                            vipEndTime={row.endTime}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Chip size="small" label={JOIN_TYPE_MAP[row.joinType] || row.joinType} variant="outlined"/>
                        </TableCell>
                        <TableCell align="left">{row.remark || "-"}</TableCell>
                        <TableCell align="left">
                          <Typography
                            variant="body2" color="primary.main" sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
                            onClick={() => openRecords(row.uid)}
                          >
                            查看
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{py: 5}}><SearchNotFound/></TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange}/>
        </Card>
      </Container>

      {/* VIP操作记录弹框 */}
      <Dialog open={recordOpen} onClose={() => setRecordOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>VIP操作记录 (UID: {recordUid})</DialogTitle>
        <DialogContent>
          {recordLoading ? (
            <Stack alignItems="center" py={3}><CircularProgress/></Stack>
          ) : records.length === 0 ? (
            <Typography color="text.secondary" py={2}>暂无记录</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <ListHead headLabel={RECORD_TABLE_HEAD}/>
                <TableBody>
                  {records.map((r, idx) => (
                    <TableRow hover key={r.id || idx}>
                      <TableCell align="left">
                        <Chip size="small" label={ACTION_MAP[r.action] || r.action} color={r.action === "GRANT" ? "success" : r.action === "REVOKE" ? "error" : "info"}/>
                      </TableCell>
                      <TableCell align="left">{RECORD_JOIN_TYPE_MAP[r.joinType] || r.joinType}</TableCell>
                      <TableCell align="left">{r.days}天</TableCell>
                      <TableCell align="left">{formatTimestamp(r.prevEndTime)}</TableCell>
                      <TableCell align="left">{formatTimestamp(r.newEndTime)}</TableCell>
                      <TableCell align="left">{r.adminName || r.addUid || "-"}</TableCell>
                      <TableCell align="left">{r.remark || "-"}</TableCell>
                      <TableCell align="left">{formatTimestamp(r.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* 用户详情弹框 */}
      {targetUser && (
        <UserInfoDrawer
          open
          uid={targetUser.uid}
          onClose={() => setTargetUser(null)}
        />
      )}
    </Page>
  );
}
